import { useMemo } from 'react';
import { css } from '@emotion/react';
import {
  AppointmentTypeGroup,
  BookableAppointmentType,
} from '@weave/schema-gen-ts/dist/schemas/schedule/v3/bookable_appointment_type.pb';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { formatCentsToCurrency } from '@frontend/currency';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Button, Table, TableColumnConfig, TableRowActions, Text } from '@frontend/design-system';

export type ServiceTypesTableProps = {
  appointmentTypeGroup: AppointmentTypeGroup;
  onCreate: (selectedAppointmentTypeGroup: SchedulerV3.Types.SelectedServiceType) => void;
  onDelete: (selectedServiceType: SchedulerV3.Types.SelectedServiceType) => void;
  onEdit: (selectedServiceType: SchedulerV3.Types.SelectedServiceType) => void;
  isPaymentsEnabled: boolean;
};

export const ServiceTypesTable = ({
  appointmentTypeGroup,
  onCreate,
  onDelete,
  onEdit,
  isPaymentsEnabled,
}: ServiceTypesTableProps) => {
  const { t } = useTranslation('scheduleServiceTypes');

  const tableColumns = useMemo(
    (): TableColumnConfig<BookableAppointmentType>[] => [
      {
        id: 'displayName',
        Header: t('Service Name'),
        accessor: (row: BookableAppointmentType) => row.displayName,
        disableSortBy: true,
        minWidth: 150,
        maxWidth: 250,
      },
      {
        id: 'duration',
        Header: t('Duration'),
        accessor: (row: BookableAppointmentType) => (row.duration ? `${row.duration} ${t('mins')}` : '-'),
        disableSortBy: true,
        width: 100,
        maxWidth: 100,
      },
      {
        id: 'description',
        Header: t('Description'),
        accessor: (row: BookableAppointmentType) => row.description,
        disableSortBy: true,
        minWidth: 200,
      },
      {
        id: 'proc',
        Header: t('Procedure Codes'),
        accessor: (_) => ['D7110', 'D0100', 'D2100'],
        cellRenderer: (value: string[]) => (value?.length ? value.join(', ') : '-'), // TODO: update accessor to get actual procedure codes when available in BE
        omit: true, // TODO: remove omit when procedure codes are available from API
        disableSortBy: true,
        minWidth: 100,
        maxWidth: 200,
      },
      {
        id: 'prepaymentAmount',
        Header: t('Pre-Pay Amount'),
        omit: !isPaymentsEnabled,
        accessor: (row: BookableAppointmentType) => row.prepaymentAmount || 0,
        disableSortBy: true,
        cellRenderer: (amount) => (amount ? formatCentsToCurrency(amount) : '-'),
        cellAlign: 'center',
        minWidth: 100,
        maxWidth: 150,
      },
    ],
    [t, isPaymentsEnabled]
  );

  const tableActionMenu: TableRowActions<BookableAppointmentType> = useMemo(() => {
    return {
      actions: [
        {
          Icon: () => <Icon name='edit-small' />,
          label: t('Edit'),
          onClick: (row) => onEdit({ appointmentTypeGroup, bookableAppointmentType: row }),
        },
        {
          Icon: () => <Icon name='trash-small' color='error' />,
          label: t('Delete'),
          css: { span: { color: theme.colors.critical50 } },
          onClick: (row: BookableAppointmentType) => onDelete({ appointmentTypeGroup, bookableAppointmentType: row }),
        },
      ],
    };
  }, [onEdit, t, onDelete, appointmentTypeGroup]);

  return (
    <>
      <Table
        colConfig={tableColumns}
        data={appointmentTypeGroup.bookableAppointmentTypes ?? []}
        rowActions={tableActionMenu}
        tableStyle={{
          name: 'service-type-v3-table',
          styles: `border-radius: 0px !important; `,
        }}
        styleConfig={{
          rows: () => {
            return css`
              min-height: ${theme.spacing(7.2)};
              border-radius: 0px;
              background-color: ${theme.colors.neutral[5]} !important;
            `;
          },
          columns: [
            {
              id: '*',
              headerStyler: () =>
                css`
                  background-color: ${theme.colors.neutral[5]} !important;
                `,
            },
          ],
        }}
        emptyStateConfig={{
          type: 'missing_forms',
          header: '',
          description: () => (
            <div css={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: theme.spacing(3) }}>
              <div css={{ textAlign: 'center', maxWidth: 336 }}>
                <Text weight='bold'>{t('No Bookable Services Yet')}</Text>
                <Text color='subdued' size='medium'>
                  {t('Create one to make this appointment type bookable for online scheduling and AI receptionist')}
                </Text>
              </div>
              <Button
                iconName='add'
                onClick={() => onCreate({ appointmentTypeGroup, bookableAppointmentType: undefined })}
              >
                {t('Add Service')}
              </Button>
            </div>
          ),
        }}
      />
    </>
  );
};
